import React, { useEffect, useRef, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import BorderLeftIcon from '@material-ui/icons/BorderLeft';
import BorderRightIcon from '@material-ui/icons/BorderRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';

const ALTURA_BARRA = 44;
const LARGURA_PADRAO = 160;
const PASSO_SETAS = 240;

const useStyles = makeStyles((theme) => ({
  raiz: {
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  // A barra e o cabeçalho ficam presos no topo da área que rola (a página ou um diálogo).
  // Por isso o cabeçalho é uma tabela separada da do corpo (que rola na horizontal).
  barra: {
    position: 'sticky',
    top: 0,
    zIndex: 6,
    height: ALTURA_BARRA,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 16px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e2e8f0',
    borderRadius: '8px 8px 0 0',
  },
  grupoSetas: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    boxSizing: 'border-box',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
  },
  cabecalho: {
    position: 'sticky',
    top: ALTURA_BARRA,
    zIndex: 5,
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
  },
  corpoWrapper: { position: 'relative' },
  // sem a barra de rolagem nativa (ela ficava no fim da tabela): a rolagem é pelas setas,
  // por arraste ou pelo trackpad.
  corpo: {
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  tabela: {
    tableLayout: 'fixed',
    width: '100%',
    // `separate` para as colunas fixadas (sticky) funcionarem em células de cabeçalho.
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  celulaCabecalho: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 500,
    padding: '10px 16px',
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    borderBottom: 'none',
    wordBreak: 'break-word',
  },
  ordenar: {
    color: 'white !important',
    '& svg': { color: 'white !important' },
  },
  celula: {
    backgroundColor: 'inherit',
    padding: '10px 16px',
    fontSize: '0.875rem',
    wordBreak: 'break-word',
  },
  // fundo opaco também no hover: as células fixadas herdam essa cor e não podem ser vazadas.
  linha: {
    backgroundColor: '#fff',
    '&.MuiTableRow-hover:hover': { backgroundColor: '#f1f5f9' },
  },
  sombra: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 36,
    pointerEvents: 'none',
    zIndex: 4,
  },
  vazio: { padding: 40, textAlign: 'center' },
  botaoColunas: {
    borderRadius: 8,
    textTransform: 'none',
    border: '1px solid #e2e8f0',
    '&:hover': { borderColor: '#cbd5e1' },
  },
  menuColunasAcoesGlobais: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 8px',
  },
  menuColunasLista: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  menuColunasItem: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' },
  },
  botaoFixarColuna: { padding: 4 },
  botaoFixarColunaAtivo: {
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    '&:hover': { backgroundColor: '#dbeafe' },
  },
}));

// Posição das colunas fixadas: `esquerda` acumula da borda esquerda, `direita` da direita.
const calcularFixacao = (colunas) => {
  const resultado = {};
  let esquerda = 0;
  colunas.forEach((coluna) => {
    if (coluna.fixar === 'esquerda') {
      resultado[coluna.chave] = { left: esquerda };
      esquerda += coluna.largura || LARGURA_PADRAO;
    }
  });
  let direita = 0;
  [...colunas].reverse().forEach((coluna) => {
    if (coluna.fixar === 'direita') {
      resultado[coluna.chave] = { right: direita };
      direita += coluna.largura || LARGURA_PADRAO;
    }
  });
  return {
    posicoes: resultado,
    larguraEsquerda: esquerda,
    larguraDireita: direita,
  };
};

const estiloFixado = (coluna, posicoes, z) =>
  coluna.fixar
    ? {
        position: 'sticky',
        zIndex: z,
        ...posicoes[coluna.chave],
        boxShadow:
          coluna.fixar === 'esquerda'
            ? '2px 0 4px -2px rgba(0,0,0,0.15)'
            : '-2px 0 4px -2px rgba(0,0,0,0.15)',
      }
    : {};

/**
 * Botões de ação de uma linha: os primeiros `maxVisiveis` como ícones, o resto num menu
 * "mais ações". Cada ação: { title, icon, onClick(registro), disabled?, show? } (title,
 * disabled e show podem ser função do registro).
 */
export const AcoesLinha = ({ acoes, registro, maxVisiveis = 3 }) => {
  const [ancora, setAncora] = useState(null);
  const valor = (campo, padrao) =>
    typeof campo === 'function' ? campo(registro) : campo ?? padrao;
  const exibidas = acoes.filter((acao) => valor(acao.show, true));
  const visiveis = exibidas.slice(0, maxVisiveis);
  const noMenu = exibidas.slice(maxVisiveis);

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      {visiveis.map((acao) => (
        <Tooltip key={valor(acao.title)} title={valor(acao.title, '')}>
          {/* span: o Tooltip precisa de um elemento que receba eventos mesmo desabilitado */}
          <span>
            <IconButton
              size="small"
              disabled={Boolean(valor(acao.disabled, false))}
              onClick={() => acao.onClick(registro)}
              {...(acao.propsButton || {})}
            >
              {acao.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
      {noMenu.length > 0 && (
        <>
          <Tooltip title="Mais ações">
            <IconButton
              size="small"
              onClick={(e) => setAncora(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={ancora}
            open={Boolean(ancora)}
            onClose={() => setAncora(null)}
          >
            {noMenu.map((acao) => (
              <MenuItem
                key={valor(acao.title)}
                disabled={Boolean(valor(acao.disabled, false))}
                onClick={() => {
                  setAncora(null);
                  acao.onClick(registro);
                }}
              >
                {acao.icon}
                <span style={{ marginLeft: 8 }}>{valor(acao.title, '')}</span>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );
};

/**
 * Tabela de dados com o mesmo visual das listas do sistema (cabeçalho na cor primária,
 * colunas fixadas, aviso de "mais colunas" com setas, barra e cabeçalho presos ao rolar),
 * sem depender do react-admin: quem usa busca os dados e controla a paginação.
 *
 * colunas: [{ chave, titulo, render(registro), largura?, fixar?: 'esquerda' | 'direita' }]
 *   (`fixar` é a fixação inicial; o botão "Colunas" deixa mostrar, ocultar e fixar cada
 *   coluna, e fixadas à esquerda/direita vão para as bordas na ordem em que aparecem)
 * ordenacao ({ chave, direcao: 'asc' | 'desc' }) e onOrdenar(chave): colunas com `ordenavel: true`
 *   mostram a seta de ordenação no cabeçalho (quem usa ordena os dados).
 * linhas, total, pagina (começa em 0), porPagina, onMudarPagina(pagina),
 * onMudarPorPagina(quantidade), carregando, vazio (conteúdo mostrado sem registros),
 * rotuloItens (ex.: "documentos"), chaveLinha (padrão "id").
 */
// Celular: cada linha vira um cartão. O título é a primeira coluna fixada à esquerda (ou a primeira),
// "status"/"pendencia" ficam em destaque, as demais viram pares rótulo/valor, e a coluna "acoes"
// vira botões em pílula quando tem `acoes` (lista de { title, icon, onClick, show, disabled,
// propsButton }): a principal (propsButton.color 'primary') vem primeiro, as duas primeiras ficam
// lado a lado e o resto vai para o menu de três pontinhos. Coluna com `soDesktop: true` não entra
// no cartão; `ordemCartao` (número) muda a posição da coluna no cartão (sem ela, vale a ordem da
// tabela) `cartaoLargo: true` faz a coluna ocupar a linha inteira e `renderCartao` troca o conteúdo da
// coluna só no cartão e `soCartao: true` faz a coluna existir só no cartão.
export const CartaoLinha = ({ colunas, linha }) => {
  const [ancora, setAncora] = useState(null);
  const titulo = colunas.find((c) => c.fixar === 'esquerda') || colunas[0];
  const colunaAcoes = colunas.find((c) => c.chave === 'acoes');
  const destaque = colunas.filter(
    (c) => c !== titulo && ['pendencia', 'status'].includes(c.chave),
  );
  const dados = colunas
    .filter(
      (c) =>
        c !== titulo &&
        c !== colunaAcoes &&
        !destaque.includes(c) &&
        !c.soDesktop,
    )
    .sort((x, y) => (x.ordemCartao || 0) - (y.ordemCartao || 0));
  const valor = (campo, padrao) =>
    typeof campo === 'function' ? campo(linha) : campo ?? padrao;
  const ePrincipal = (acao) =>
    Boolean(acao.propsButton && acao.propsButton.color === 'primary');
  const itens = ((colunaAcoes && colunaAcoes.acoes) || [])
    .filter((acao) => valor(acao.show, true))
    .map((acao) => ({
      acao,
      principal: ePrincipal(acao),
      sempre: Boolean(acao.sempreNoCartao),
      desabilitada: Boolean(valor(acao.disabled, false)),
      // `rotuloCurto` da ação (se houver) é o texto do botão no cartão; o título vira dica na tabela.
      titulo: acao.rotuloCurto || valor(acao.title, ''),
    }))
    // No cartão não precisa alinhar colunas: ação desabilitada some, exceto a que tem
    // `sempreNoCartao` (ex.: "Detalhes"), que fica sempre à vista.
    .filter(({ sempre, desabilitada }) => sempre || !desabilitada)
    // Primeiro a principal, depois a que fica sempre à vista e por fim as outras.
    .sort(
      (x, y) =>
        Number(y.principal) * 2 +
        Number(y.sempre) -
        (Number(x.principal) * 2 + Number(x.sempre)),
    );
  const botoes = itens.slice(0, 2);
  const menu = itens.slice(2);
  const linhaDivisoria = {
    height: 1,
    backgroundColor: '#e3e3e3',
    margin: '12px 0',
  };

  return (
    <Box
      style={{
        backgroundColor: '#f6f6f6',
        border: '1px solid #d9d9d9',
        borderRadius: 18,
        padding: '16px 18px',
        marginBottom: 14,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Typography
          variant="subtitle1"
          component="div"
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
            color: '#111',
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {titulo && titulo.render(linha)}
        </Typography>
        {menu.length > 0 && (
          <>
            <IconButton
              aria-label="Mais ações"
              size="small"
              style={{
                flexShrink: 0,
                width: 36,
                height: 36,
                border: '1px solid #cfcfcf',
                color: '#333',
              }}
              onClick={(e) => setAncora(e.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={ancora}
              open={Boolean(ancora)}
              onClose={() => setAncora(null)}
            >
              {menu.map(({ acao, desabilitada, titulo: rotulo }) => (
                <MenuItem
                  key={rotulo}
                  disabled={desabilitada}
                  onClick={() => {
                    setAncora(null);
                    acao.onClick(linha);
                  }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    {acao.icon}
                    {rotulo}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      {destaque.length > 0 && (
        <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          {destaque.map((c) => (
            <div key={c.chave}>{(c.renderCartao || c.render)(linha)}</div>
          ))}
        </Box>
      )}
      {dados.length > 0 && (
        <>
          <div style={linhaDivisoria} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 12px',
            }}
          >
            {dados.map((c) => (
              <div
                key={c.chave}
                style={{
                  minWidth: 0,
                  gridColumn: c.cartaoLargo ? '1 / -1' : undefined,
                }}
              >
                <Typography
                  variant="caption"
                  display="block"
                  style={{
                    color: '#777',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                  }}
                >
                  {c.titulo}
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  style={{
                    wordBreak: 'break-word',
                    fontWeight: 700,
                    color: '#111',
                  }}
                >
                  {(c.renderCartao || c.render)(linha)}
                </Typography>
              </div>
            ))}
          </div>
        </>
      )}
      {botoes.length > 0 && (
        <>
          <div style={linhaDivisoria} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: botoes.length === 1 ? '1fr' : '1fr 1fr',
              gap: 8,
            }}
          >
            {botoes.map(
              ({ acao, principal, desabilitada, titulo: rotulo }, posicao) => (
                <Button
                  key={rotulo}
                  variant={posicao === 0 ? 'contained' : 'outlined'}
                  startIcon={acao.icon}
                  disabled={desabilitada}
                  onClick={() => acao.onClick(linha)}
                  style={{
                    minHeight: 44,
                    minWidth: 0,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    ...(posicao === 0 && !desabilitada
                      ? { backgroundColor: '#111', color: '#fff' }
                      : {
                          borderColor: '#cfcfcf',
                          color: desabilitada ? undefined : '#333',
                        }),
                  }}
                >
                  {rotulo}
                </Button>
              ),
            )}
          </div>
        </>
      )}
      {colunaAcoes && !colunaAcoes.acoes && (
        <>
          <div style={linhaDivisoria} />
          {colunaAcoes.render(linha)}
        </>
      )}
    </Box>
  );
};

const TabelaDados = ({
  colunas: colunasBase,
  linhas,
  ordenacao = null,
  onOrdenar = null,
  total,
  pagina,
  porPagina,
  onMudarPagina,
  onMudarPorPagina,
  carregando = false,
  vazio = 'Nenhum registro encontrado.',
  rotuloItens = 'registros',
  chaveLinha = 'id',
  rowsPerPageOptions = [25, 50, 100],
}) => {
  const classes = useStyles();
  const celular = useMediaQuery((tema) => tema.breakpoints.down('sm'));
  const refCorpo = useRef(null);
  const refCabecalho = useRef(null);
  const [bordas, setBordas] = useState({ esquerda: false, direita: false });
  const [ancoraColunas, setAncoraColunas] = useState(null);
  const [ocultas, setOcultas] = useState({});
  const [fixadas, setFixadas] = useState(() =>
    Object.fromEntries(
      colunasBase.filter((c) => c.fixar).map((c) => [c.chave, c.fixar]),
    ),
  );
  // colunas visíveis, com as fixadas à esquerda primeiro e as da direita por último.
  const comLado = colunasBase
    .filter((coluna) => !ocultas[coluna.chave])
    .map((coluna) => ({ ...coluna, fixar: fixadas[coluna.chave] }));
  const ordenarPorLado = (lista) => [
    ...lista.filter((coluna) => coluna.fixar === 'esquerda'),
    ...lista.filter((coluna) => !coluna.fixar),
    ...lista.filter((coluna) => coluna.fixar === 'direita'),
  ];
  // Coluna com `soCartao: true` só existe no cartão do celular (não vai para a tabela).
  const colunas = ordenarPorLado(comLado.filter((coluna) => !coluna.soCartao));
  const colunasCartao = ordenarPorLado(comLado);
  const { posicoes, larguraEsquerda, larguraDireita } = calcularFixacao(
    colunas,
  );
  const larguraTotal = colunas.reduce(
    (soma, coluna) => soma + (coluna.largura || LARGURA_PADRAO),
    0,
  );
  const semRegistros = !carregando && linhas.length === 0;

  useEffect(() => {
    const el = refCorpo.current;
    if (!el) return undefined;
    const atualizar = () => {
      if (refCabecalho.current) refCabecalho.current.scrollLeft = el.scrollLeft;
      const esquerda = el.scrollLeft > 1;
      const direita = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setBordas((atual) =>
        atual.esquerda === esquerda && atual.direita === direita
          ? atual
          : { esquerda, direita },
      );
    };
    atualizar();
    el.addEventListener('scroll', atualizar, { passive: true });
    window.addEventListener('resize', atualizar);
    const observador =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(atualizar)
        : null;
    if (observador) observador.observe(el);
    return () => {
      el.removeEventListener('scroll', atualizar);
      window.removeEventListener('resize', atualizar);
      if (observador) observador.disconnect();
    };
  }, [semRegistros, linhas, colunas.length]);

  function alternarColuna(chave) {
    setOcultas((atual) => ({ ...atual, [chave]: !atual[chave] }));
  }

  function fixarColuna(chave, lado) {
    setFixadas((atual) => {
      const novo = { ...atual };
      if (novo[chave] === lado) delete novo[chave];
      else novo[chave] = lado;
      return novo;
    });
  }

  function rolar(sentido) {
    if (refCorpo.current) {
      refCorpo.current.scrollBy({
        left: sentido * PASSO_SETAS,
        behavior: 'smooth',
      });
    }
  }

  const tabela = (children) => (
    <Table
      className={classes.tabela}
      style={{ minWidth: larguraTotal }}
      size="small"
    >
      <colgroup>
        {colunas.map((coluna) => (
          <col
            key={coluna.chave}
            style={{ width: coluna.largura || LARGURA_PADRAO }}
          />
        ))}
      </colgroup>
      {children}
    </Table>
  );

  if (celular) {
    const ordenaveis = colunasBase.filter((c) => c.ordenavel);
    return (
      <div>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
          style={{ gap: 8 }}
        >
          <Typography variant="body2" color="textSecondary">
            {carregando ? 'Carregando...' : `${total} ${rotuloItens}`}
          </Typography>
          {onOrdenar && ordenaveis.length > 0 && (
            <Box display="flex" alignItems="center">
              <TextField
                select
                size="small"
                variant="outlined"
                value={ordenacao ? ordenacao.chave : ''}
                onChange={(e) => onOrdenar(e.target.value)}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Ordenar por
                </MenuItem>
                {ordenaveis.map((c) => (
                  <MenuItem key={c.chave} value={c.chave}>
                    {c.titulo}
                  </MenuItem>
                ))}
              </TextField>
              {ordenacao && (
                <Tooltip title="Inverter a ordem">
                  <IconButton
                    size="small"
                    onClick={() => onOrdenar(ordenacao.chave)}
                  >
                    {ordenacao.direcao === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        {carregando && <LinearProgress />}
        {semRegistros ? (
          <div className={classes.vazio}>{vazio}</div>
        ) : (
          <>
            {linhas.map((linha) => (
              <CartaoLinha
                key={linha[chaveLinha]}
                colunas={colunasCartao}
                linha={linha}
              />
            ))}
            <TablePagination
              component="div"
              count={total}
              page={pagina}
              rowsPerPage={porPagina}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
              onChangePage={(e, nova) => onMudarPagina(nova)}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <Paper variant="outlined" className={classes.raiz}>
      <div className={classes.barra}>
        <Button
          size="small"
          startIcon={<ViewColumnIcon />}
          endIcon={<ArrowDropDownIcon />}
          className={classes.botaoColunas}
          onClick={(e) => setAncoraColunas(e.currentTarget)}
        >
          Colunas
        </Button>
        <Menu
          anchorEl={ancoraColunas}
          open={Boolean(ancoraColunas)}
          onClose={() => setAncoraColunas(null)}
        >
          <Box className={classes.menuColunasAcoesGlobais}>
            <Button size="small" onClick={() => setOcultas({})}>
              Marcar todas
            </Button>
            <Button
              size="small"
              disabled={Object.keys(fixadas).length === 0}
              onClick={() => setFixadas({})}
            >
              Desfixar todas
            </Button>
          </Box>
          <Divider />
          <Box className={classes.menuColunasLista}>
            {colunasBase
              .filter((coluna) => !coluna.soCartao)
              .map((coluna) => (
                <MenuItem
                  key={coluna.chave}
                  dense
                  className={classes.menuColunasItem}
                  onClick={() => alternarColuna(coluna.chave)}
                >
                  <FormControlLabel
                    onClick={(e) => e.stopPropagation()}
                    control={
                      <Checkbox
                        size="small"
                        checked={!ocultas[coluna.chave]}
                        onChange={() => alternarColuna(coluna.chave)}
                      />
                    }
                    label={coluna.titulo}
                  />
                  <Box display="flex" alignItems="center">
                    {[
                      [
                        'esquerda',
                        'Fixar à esquerda',
                        <BorderLeftIcon fontSize="small" />,
                      ],
                      [
                        'direita',
                        'Fixar à direita',
                        <BorderRightIcon fontSize="small" />,
                      ],
                    ].map(([lado, dica, icone]) => (
                      <Tooltip key={lado} title={dica}>
                        <IconButton
                          size="small"
                          className={`${classes.botaoFixarColuna} ${
                            fixadas[coluna.chave] === lado
                              ? classes.botaoFixarColunaAtivo
                              : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            fixarColuna(coluna.chave, lado);
                          }}
                        >
                          {icone}
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                </MenuItem>
              ))}
          </Box>
        </Menu>
        <Typography variant="body2" color="textSecondary">
          {carregando ? 'Carregando...' : `${total} ${rotuloItens}`}
        </Typography>
        <div style={{ flex: 1 }} />
        {(bordas.esquerda || bordas.direita) && (
          <Typography
            variant="caption"
            color="primary"
            style={{ fontWeight: 600 }}
          >
            {bordas.direita
              ? 'Há mais colunas à direita →'
              : '← Há mais colunas à esquerda'}
          </Typography>
        )}
        <div className={classes.grupoSetas}>
          <IconButton
            size="small"
            color={bordas.esquerda ? 'primary' : 'default'}
            disabled={!bordas.esquerda}
            onClick={() => rolar(-1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <IconButton
            size="small"
            color={bordas.direita ? 'primary' : 'default'}
            disabled={!bordas.direita}
            onClick={() => rolar(1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {semRegistros ? (
        <div className={classes.vazio}>{vazio}</div>
      ) : (
        <>
          <div
            ref={refCabecalho}
            className={classes.cabecalho}
            onWheel={(e) => {
              if (refCorpo.current) refCorpo.current.scrollLeft += e.deltaX;
            }}
          >
            {tabela(
              <TableHead>
                <TableRow>
                  {colunas.map((coluna) => (
                    <TableCell
                      key={coluna.chave}
                      className={classes.celulaCabecalho}
                      style={estiloFixado(coluna, posicoes, 3)}
                    >
                      {coluna.ordenavel && onOrdenar ? (
                        <TableSortLabel
                          className={classes.ordenar}
                          active={
                            Boolean(ordenacao) &&
                            ordenacao.chave === coluna.chave
                          }
                          direction={
                            ordenacao && ordenacao.chave === coluna.chave
                              ? ordenacao.direcao
                              : 'asc'
                          }
                          onClick={() => onOrdenar(coluna.chave)}
                        >
                          {coluna.titulo}
                        </TableSortLabel>
                      ) : (
                        coluna.titulo
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>,
            )}
          </div>
          {carregando && <LinearProgress />}
          <div className={classes.corpoWrapper}>
            <div ref={refCorpo} className={classes.corpo}>
              {tabela(
                <TableBody>
                  {linhas.map((linha) => (
                    <TableRow
                      key={linha[chaveLinha]}
                      hover
                      className={classes.linha}
                    >
                      {colunas.map((coluna) => (
                        <TableCell
                          key={coluna.chave}
                          className={classes.celula}
                          style={estiloFixado(coluna, posicoes, 2)}
                        >
                          {coluna.render(linha)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>,
              )}
            </div>
            {bordas.direita && (
              <div
                className={classes.sombra}
                style={{
                  right: larguraDireita,
                  background:
                    'linear-gradient(to left, rgba(0,0,0,0.16), rgba(0,0,0,0))',
                }}
              />
            )}
            {bordas.esquerda && (
              <div
                className={classes.sombra}
                style={{
                  left: larguraEsquerda,
                  background:
                    'linear-gradient(to right, rgba(0,0,0,0.16), rgba(0,0,0,0))',
                }}
              />
            )}
          </div>
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            rowsPerPage={porPagina}
            rowsPerPageOptions={rowsPerPageOptions}
            labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            onChangePage={(e, nova) => onMudarPagina(nova)}
            onChangeRowsPerPage={(e) =>
              onMudarPorPagina(Number(e.target.value))
            }
          />
        </>
      )}
    </Paper>
  );
};

export default TabelaDados;
